import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBrand } from '../config/brands';
import {
  ChevronDown, ChevronUp, Shield, FileText, Clock, MessageCircle,
  CreditCard, Stethoscope, Heart, Activity, Pill, UserCheck, Phone
} from 'lucide-react';

const brand = getBrand();

const treatmentIcons = [Stethoscope, Heart, Activity, Pill, Shield, UserCheck];

const faqs = [
  { q: 'A teleconsulta com urologista é segura?', a: 'Sim. Utilizamos plataforma criptografada com sigilo médico-paciente garantido conforme o Código de Ética Médica do CFM. Seus dados são protegidos pela LGPD.' },
  { q: 'Como recebo minhas receitas?', a: 'As receitas e pedidos de exames são enviados com assinatura digital certificada (ICP-Brasil), válidas em qualquer farmácia e laboratório do Brasil.' },
  { q: 'Preciso baixar algum aplicativo?', a: 'Não. O atendimento é feito diretamente pelo navegador do seu celular ou computador, sem instalar nada.' },
  { q: 'Quanto custa a consulta?', a: 'A consulta particular com o urologista custa R$ 190. Aceitamos cartão de crédito ou PIX à vista. Não há mensalidade.' },
  { q: 'A consulta serve para renovar receitas?', a: 'Sim. Se você já possui um tratamento em andamento e precisa renovar a receita, a teleconsulta é ideal para isso. O médico avaliará e emitirá a nova prescrição.' },
  { q: 'O atendimento é feito por qual médico?', a: 'Todas as consultas são realizadas pelo Dr. Rodrigo Seben, urologista com CRM/RS 30092, especialista em saúde do homem, reposição hormonal e urgências urológicas.' },
  { q: 'Quais formas de pagamento são aceitas?', a: 'Aceitamos cartão de crédito (Visa, Mastercard, Elo, Amex) e PIX para pagamento à vista.' },
];

// Colors matching the doctor's Wix site
const C = {
  teal: '#5B9EA6',
  tealLight: '#6DB3BB',
  tealDark: '#4A8B93',
  navy: '#2D4A6F',
  navyDark: '#1E3550',
  white: '#FFFFFF',
  grayLight: '#F4F7F9',
  grayText: '#5A6B7A',
  dark: '#1A2A3A',
};

export default function UrologiaLanding() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const handleCTA = () => navigate('/cadastro');

  return (
    <main className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <Helmet>
        <title>Urologista Online - Teleconsulta com Especialista | R$ 190</title>
        <meta name="description" content="Consulte um urologista online agora. Dr. Rodrigo Seben CRM/RS 30092. Teleconsulta particular por R$ 190. Receita digital válida. Atendimento 7 dias por semana." />
        <meta name="keywords" content="urologista online, teleconsulta urologia, consulta urologista online, urologista por chat, receita digital urologista, Dr. Rodrigo Seben, CRM/RS 30092, urologia online, consulta particular urologista, reposição hormonal masculina" />
        <link rel="canonical" href="https://uronline.com.br/" />
        <meta property="og:title" content="Urologista Online — Teleconsulta com Especialista por R$ 190" />
        <meta property="og:description" content="Consulte o Dr. Rodrigo Seben (CRM/RS 30092) por chat. Receita digital e pedidos de exames válidos. 7 dias por semana." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://uronline.com.br/" />
        <meta property="og:image" content="https://static.wixstatic.com/media/5df835_c688ae377a324fffa38ad9802709461e~mv2.jpg" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Urologista Online" />
        <meta property="article:modified_time" content="2026-06-26T00:00:00Z" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Urologista Online",
          "url": "https://uronline.com.br",
          "description": "O Urologista Online é uma plataforma de teleconsulta particular com urologista especialista. O atendimento é realizado por chat em tempo real com o Dr. Rodrigo Seben (CRM/RS 30092), com emissão de receita digital e pedidos de exames válidos em todo o Brasil. A consulta custa R$ 190 e pode ser paga por cartão de crédito ou PIX à vista.",
          "image": "https://static.wixstatic.com/media/5df835_c688ae377a324fffa38ad9802709461e~mv2.jpg",
          "priceRange": "R$ 190",
          "currenciesAccepted": "BRL",
          "paymentAccepted": "Cartão de Crédito, PIX",
          "telephone": "+55-51-3376-5090",
          "email": "contato@clicdoc.com.br",
          "foundingDate": "2026",
          "dateModified": "2026-06-26",
          "medicalSpecialty": "https://schema.org/Urologic",
          "areaServed": {"@type": "Country", "name": "Brazil", "sameAs": "https://en.wikipedia.org/wiki/Brazil"},
          "address": {"@type": "PostalAddress", "addressCountry": "BR", "addressRegion": "RS"},
          "openingHoursSpecification": {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "00:00", "closes": "23:59"},
          "availableService": {"@type": "MedicalProcedure", "name": "Teleconsulta com Urologista", "procedureType": "https://schema.org/NoninvasiveProcedure", "howPerformed": "Atendimento por chat em tempo real com urologista especialista. O paciente descreve seus sintomas, o médico realiza a anamnese e pode emitir receita digital e pedidos de exames com assinatura eletrônica qualificada.", "bodyLocation": "Sistema urinário e reprodutor masculino"},
          "offers": {"@type": "Offer", "name": "Teleconsulta com Urologista", "price": "190.00", "priceCurrency": "BRL", "availability": "https://schema.org/InStock", "url": "https://uronline.com.br/cadastro", "description": "Consulta particular com urologista por chat. Inclui receita digital e pedidos de exames válidos."},
          "sameAs": ["https://wa.me/555133765090"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Physician",
          "name": "Dr. Rodrigo Seben",
          "url": "https://uronline.com.br",
          "image": "https://static.wixstatic.com/media/5df835_c688ae377a324fffa38ad9802709461e~mv2.jpg",
          "description": "Urologista especialista em saúde do homem, reposição hormonal masculina e urgências urológicas. Atendimento por teleconsulta via chat com receita digital e pedidos de exames.",
          "medicalSpecialty": "https://schema.org/Urologic",
          "identifier": {"@type": "PropertyValue", "name": "CRM", "value": "CRM/RS 30092"},
          "knowsAbout": ["Urologia", "Saúde do Homem", "Reposição Hormonal", "Doenças da Próstata", "Infecções Urinárias", "ISTs", "Disfunção Erétil", "Varicocele"],
          "availableService": [
            {"@type": "MedicalProcedure", "name": "Teleconsulta Urológica"},
            {"@type": "MedicalProcedure", "name": "Prescrição Digital de Medicamentos"},
            {"@type": "MedicalProcedure", "name": "Pedidos de Exames Laboratoriais e Imagem"}
          ],
          "address": {"@type": "PostalAddress", "addressCountry": "BR", "addressRegion": "RS"}
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "dateModified": "2026-06-26",
          "mainEntity": [
            {"@type": "Question", "name": "A teleconsulta com urologista é segura?", "acceptedAnswer": {"@type": "Answer", "text": "Sim. Utilizamos plataforma criptografada com sigilo médico-paciente garantido conforme o Código de Ética Médica do CFM. Seus dados são protegidos pela LGPD. As receitas possuem assinatura digital certificada ICP-Brasil."}},
            {"@type": "Question", "name": "Como recebo minhas receitas e pedidos de exames?", "acceptedAnswer": {"@type": "Answer", "text": "As receitas e pedidos de exames são enviados diretamente pelo chat com assinatura digital certificada ICP-Brasil, válidas em qualquer farmácia e laboratório do Brasil. Você também recebe por SMS."}},
            {"@type": "Question", "name": "Quanto custa a consulta com urologista online?", "acceptedAnswer": {"@type": "Answer", "text": "A consulta particular com o urologista custa R$ 190. Aceitamos cartão de crédito ou PIX à vista. Não há mensalidade nem taxa de adesão."}},
            {"@type": "Question", "name": "Preciso baixar algum aplicativo?", "acceptedAnswer": {"@type": "Answer", "text": "Não. O atendimento é feito diretamente pelo navegador do seu celular ou computador, sem instalar nada. Basta acessar uronline.com.br."}},
            {"@type": "Question", "name": "O atendimento é feito por qual médico?", "acceptedAnswer": {"@type": "Answer", "text": "Todas as consultas são realizadas pelo Dr. Rodrigo Seben, urologista com CRM/RS 30092, especialista em saúde do homem, reposição hormonal e urgências urológicas."}},
            {"@type": "Question", "name": "A consulta serve para renovar receitas?", "acceptedAnswer": {"@type": "Answer", "text": "Sim. Se você já possui um tratamento em andamento e precisa renovar a receita, a teleconsulta é ideal para isso. O médico avaliará seu caso e emitirá a nova prescrição digital."}},
            {"@type": "Question", "name": "Quais formas de pagamento são aceitas?", "acceptedAnswer": {"@type": "Answer", "text": "Aceitamos cartão de crédito (Visa, Mastercard, Elo, Amex) e PIX para pagamento à vista. O pagamento é processado de forma segura."}}
          ]
        })}</script>
      </Helmet>

      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100" data-testid="uro-navbar">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="text-lg font-semibold" style={{ color: C.navy, fontFamily: "'Playfair Display', serif" }}>
            Urologista Online
          </span>
          <div className="hidden md:flex items-center gap-8">
            {['Tratamentos', 'Como Funciona', 'Valores', 'FAQ'].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(' ', '-')}`} className="text-sm transition-colors" style={{ color: C.grayText }} onMouseEnter={e => e.target.style.color = C.teal} onMouseLeave={e => e.target.style.color = C.grayText}>
                {item}
              </a>
            ))}
          </div>
          <button onClick={handleCTA} className="px-5 py-2 rounded-md text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.teal }} data-testid="uro-cta-nav">
            Consultar Agora
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)` }} data-testid="uro-hero" aria-label="Teleconsulta com urologista">
        <div className="max-w-6xl mx-auto px-4 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 relative z-10">
          <div className="flex-1 max-w-xl">
            <p className="uppercase text-xs tracking-[0.2em] font-bold mb-4 text-white/80">
              Teleconsulta com Especialista
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-none mb-6 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Sua saúde urológica com um especialista.
            </h1>
            <p className="text-base lg:text-lg leading-relaxed mb-3 text-white/90">
              O Urologista Online oferece teleconsulta particular com o Dr. Rodrigo Seben, urologista com CRM/RS 30092. O atendimento é feito por chat em tempo real, diretamente pelo navegador, com emissão de receita digital e pedidos de exames válidos em todo o Brasil.
            </p>
            <p className="text-sm leading-relaxed mb-8 text-white/70">
              Teleatendimento 7 dias por semana. Especialista em saúde do homem, reposição hormonal e urgências urológicas. Pagamento por cartão ou PIX.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={handleCTA} className="px-8 py-4 rounded-md text-base font-semibold transition-all hover:brightness-110 text-white" style={{ background: C.navy }} data-testid="uro-cta-hero">
                Consultar Agora — R$ 190
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 flex-wrap">
              {[{ icon: Shield, text: 'Sigilo médico' }, { icon: FileText, text: 'Receita digital' }, { icon: Clock, text: '7 dias/semana' }].map(item => (
                <div key={item.text} className="flex items-center gap-2">
                  <item.icon className="w-4 h-4 text-white/80" strokeWidth={1.5} />
                  <span className="text-sm text-white/80">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex-1 max-w-lg">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
              <img
                src={brand.doctorImage}
                alt="Dr. Rodrigo Seben - Urologista"
                className="w-full h-auto object-cover"
                style={{ minHeight: '420px' }}
                loading="eager"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: `linear-gradient(transparent, ${C.navyDark}ee)` }}>
                <p className="font-semibold text-white">Dr. Rodrigo Seben</p>
                <p className="text-sm text-white/80">CRM/RS 30092 · RQE 24120 — Urologista</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section id="tratamentos" className="py-20" style={{ background: C.grayLight }} data-testid="uro-treatments" aria-label="Tratamentos">
        <div className="max-w-6xl mx-auto px-4">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-center mb-3" style={{ color: C.teal }}>Especialidades</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4" style={{ fontFamily: "'Playfair Display', serif", color: C.navy }}>
            O que o Urologista trata?
          </h2>
          <p className="text-center max-w-2xl mx-auto mb-14 text-base" style={{ color: C.grayText }}>
            A urologia é a especialidade médica que cuida do sistema urinário de homens e mulheres, e do sistema reprodutor masculino. O Dr. Rodrigo Seben atende as seguintes condições por teleconsulta:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brand.treatments?.map((item, i) => {
              const Icon = treatmentIcons[i % treatmentIcons.length];
              return (
                <div key={item.title} className="p-6 rounded-lg bg-white border border-gray-100 shadow-sm transition-all hover:shadow-md hover:border-teal-200">
                  <Icon className="w-6 h-6 mb-3" style={{ color: C.teal }} strokeWidth={1.5} />
                  <h3 className="font-semibold mb-1" style={{ color: C.navy }}>{item.title}</h3>
                  <p className="text-sm" style={{ color: C.grayText }}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-20 bg-white" data-testid="uro-how-it-works" aria-label="Como funciona">
        <div className="max-w-6xl mx-auto px-4">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-center mb-3" style={{ color: C.teal }}>Processo</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4" style={{ fontFamily: "'Playfair Display', serif", color: C.navy }}>
            Como funciona a teleconsulta
          </h2>
          <p className="text-center max-w-2xl mx-auto mb-14 text-base" style={{ color: C.grayText }}>
            O atendimento com o urologista online é simples, rápido e seguro. Em poucos passos você estará em consulta com um especialista.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Cadastre-se', desc: 'Preencha seus dados básicos de forma rápida e segura.', icon: UserCheck },
              { step: '02', title: 'Descreva o motivo', desc: 'Conte ao urologista o que está sentindo para agilizar o atendimento.', icon: MessageCircle },
              { step: '03', title: 'Realize o pagamento', desc: 'R$ 190 no cartão ou PIX à vista.', icon: CreditCard },
              { step: '04', title: 'Inicie o chat', desc: 'Converse em tempo real com o Dr. Rodrigo Seben pelo navegador.', icon: Stethoscope },
            ].map(item => (
              <div key={item.step} className="p-6 rounded-lg text-center border border-gray-100" style={{ background: C.grayLight }}>
                <span className="inline-block text-2xl font-bold mb-3" style={{ color: C.teal, fontFamily: "'Playfair Display', serif" }}>{item.step}</span>
                <item.icon className="w-8 h-8 mx-auto mb-3" style={{ color: C.navy }} strokeWidth={1.5} />
                <h3 className="font-semibold mb-2" style={{ color: C.navy }}>{item.title}</h3>
                <p className="text-sm" style={{ color: C.grayText }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="valores" className="py-20" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)` }} data-testid="uro-pricing" aria-label="Valores">
        <div className="max-w-lg mx-auto px-4 text-center">
          <p className="uppercase text-xs tracking-[0.2em] font-bold mb-3" style={{ color: C.tealLight }}>Investimento</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Investimento em sua saúde
          </h2>
          <div className="p-8 rounded-xl mt-8 bg-white/10 backdrop-blur border border-white/20">
            <p className="text-sm mb-2 text-white/70">Consulta particular com urologista</p>
            <p className="text-5xl font-bold mb-1 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>R$ 190</p>
            <p className="text-sm mb-6 text-white/50">Valor único · Sem mensalidade</p>
            <div className="space-y-3 text-left mb-8">
              {[
                'Consulta completa com urologista por chat',
                'Receita digital com assinatura ICP-Brasil',
                'Pedidos de exames válidos em todo Brasil',
                'Atestado médico digital quando necessário',
                'Cartão ou PIX',
              ].map(item => (
                <div key={item} className="flex items-center gap-3">
                  <Shield className="w-4 h-4 flex-shrink-0" style={{ color: C.tealLight }} strokeWidth={1.5} />
                  <span className="text-sm text-white/90">{item}</span>
                </div>
              ))}
            </div>
            <button onClick={handleCTA} className="w-full py-4 rounded-md text-base font-semibold text-white transition-all hover:brightness-110" style={{ background: C.teal }} data-testid="uro-cta-pricing">
              Consultar Agora
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-white" data-testid="uro-faq" aria-label="Perguntas frequentes">
        <div className="max-w-2xl mx-auto px-4">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-center mb-3" style={{ color: C.teal }}>FAQ</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-12" style={{ fontFamily: "'Playfair Display', serif", color: C.navy }}>
            Dúvidas Frequentes
          </h2>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={faq.q} className="border-b border-gray-100">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-4 text-left transition-colors"
                  data-testid={`uro-faq-${i}`}
                >
                  <span className="font-medium text-sm pr-4" style={{ color: openFaq === i ? C.teal : C.navy }}>{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: C.teal }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: C.grayText }} />}
                </button>
                {openFaq === i && (
                  <p className="pb-4 text-sm leading-relaxed" style={{ color: C.grayText }}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: C.navyDark }} data-testid="uro-footer">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="font-semibold mb-1 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Urologista Online</p>
          <p className="text-sm mb-2 text-white/70">Dr. Rodrigo Seben — CRM/RS 30092 · RQE 24120</p>
          <p className="text-xs text-white/50">
            CNPJ: 15.472.291/0001-98 · Atendimento:{' '}
            <a href="https://wa.me/555133765090" className="underline text-white/60 hover:text-white">(51) 3376-5090</a>
          </p>
          <p className="text-xs mt-2 text-white/40">
            © 2026 Urologista Online. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </main>
  );
}
